import { Dispatch, SetStateAction, useState, useEffect } from "react";
import { Box, Grid } from "@mui/material";
import CustomTextField from "../../components/Inputs/CustomTextField";
import { makeStyles } from "@mui/styles";
import CustomDatePicker from "../../components/Inputs/CustomDatePicker";
import CustomRadioButtons from "../../components/Inputs/CustomRadioButtons";
import CustomSelect from "../../components/Inputs/CustomSelect";
import CustomAutocomplete from "../../components/Inputs/CustomAutocomplete";
import { IFormState } from "../../states/FormState";

interface Props {
  values: IFormState;
  setValues: Dispatch<SetStateAction<IFormState>>;
  errors: any;
  countries: { id: number; name: string }[];
  setCountries: Dispatch<SetStateAction<{ id: number; name: string }[]>>;
  states: { id: number; name: string }[];
  setStates: Dispatch<SetStateAction<{ id: number; name: string }[]>>;
  cities: { id: number; name: string }[];
  setCities: Dispatch<SetStateAction<{ id: number; name: string }[]>>;
  jobTypes: { id: number; name: string }[];
  setJobTypes: Dispatch<SetStateAction<{ id: number; name: string }[]>>;
  designations: { id: number; name: string }[];
  setDesignations: Dispatch<SetStateAction<{ id: number; name: string }[]>>;
}

const useStyles = makeStyles(() => ({
  form: {
    padding: "10px 0",
  },
}));

function ApplicantDetailsForm({
  values,
  setValues,
  errors,
  countries,
  setCountries,
  states,
  setStates,
  cities,
  setCities,
  jobTypes,
  setJobTypes,
  designations,
  setDesignations,
}: Props) {
  const classes = useStyles();

  const [country, setCountry] = useState<{ id: number; name: string }>(
    values.applicant.country
  );

  const [state, setState] = useState<{ id: number; name: string }>(
    values.applicant.state
  );

  const [city, setCity] = useState<{ id: number; name: string }>(
    values.applicant.city
  );

  const [jobType, setJobType] = useState<{ id: number; name: string }>(
    values.applicant.headline
  );

  const [designation, setDesignation] = useState<{ id: number; name: string }>(
    values.applicant.designationId
  );

  const handleChange = (e: any) => {
    setValues((prev) => ({
      ...prev,
      applicant: {
        ...prev.applicant,
        [e.target.name]: e.target.value,
      },
    }));
  };

  const handleDateChange = (key: string, val: Date | null) => {
    setValues((prev) => ({
      ...prev,
      applicant: {
        ...prev.applicant,
        [key]: val,
      },
    }));
  };

  // make countries array
  useEffect(() => {
    fetch(
      `https://acharyajava.uz/AcharyaInstituteUZB/api/Country`
    )
      .then((res) => res.json())
      .then((data) => {
        setCountries(
          data.map((obj: any) => ({
            id: obj.id,
            name: obj.name,
          }))
        );
      });
  }, []);
  // make states array
  useEffect(() => {
    if (country)
      fetch(
        `https://acharyajava.uz/AcharyaInstituteUZB/api/State1/${country.id}`
      )
        .then((res) => res.json())
        .then((data) => {
          setStates(data.map((obj: any) => ({ id: obj.id, name: obj.name })));
        });
  }, [country]);
  // make cities array
  useEffect(() => {
    if (country && state)
      fetch(
        `https://acharyajava.uz/AcharyaInstituteUZB/api/City1/${state.id}/${country.id}`
      )
        .then((res) => res.json())
        .then((data) => {
          setCities(data.map((obj: any) => ({ id: obj.id, name: obj.name })));
        });
  }, [country, state]);

  // update country, initialise state and city on country change
  useEffect(() => {
    if (country) {
      setValues((prev) => ({
        ...prev,
        applicant: {
          ...prev.applicant,
          country: country,
          state: null,
          city: null,
        },
      }));
      setState(null);
      setStates([]);
      setCity(null);
      setCities([]);
    }
  }, [country]);
  // update state, initialise city on state change
  useEffect(() => {
    if (state) {
      setValues((prev) => ({
        ...prev,
        applicant: { ...prev.applicant, state: state, city: null },
      }));
      setCity(null);
      setCities([]);
    }
  }, [state]);
  // update city
  useEffect(() => {
    if (city)
      setValues((prev) => ({
        ...prev,
        applicant: { ...prev.applicant, city: city },
      }));
  }, [city]);

  // make job type array
  useEffect(() => {
    fetch(
      `https://acharyajava.uz/AcharyaInstituteUZB/api/employee/JobType`
    )
      .then((res) => res.json())
      .then((data) => {
        setJobTypes(
          data.data.map((obj: any) => ({
            id: obj.job_type_id,
            name: obj.job_type,
          }))
        );
      });
  }, []);

  // update job type
  useEffect(() => {
    if (jobType)
      setValues((prev) => ({
        ...prev,
        applicant: { ...prev.applicant, headline: jobType },
      }));
  }, [jobType]);

  // make cities array
  useEffect(() => {
    if (jobType)
      fetch(
        `https://acharyajava.uz/AcharyaInstituteUZB/api/employee/getDesignation/${jobType.id}`
      )
        .then((res) => res.json())
        .then((data) => {
          setDesignations(
            data.data.map((obj: any) => ({
              id: obj.designation_id,
              name: obj.designation_name,
            }))
          );
        });
  }, [jobType]);

  // update designation
  useEffect(() => {
    if (designation)
      setValues((prev) => ({
        ...prev,
        applicant: { ...prev.applicant, designationId: designation },
      }));
  }, [designation]);

  return (
    <Box component="form" className={classes.form}>
      <Grid
        container
        justifyContent="flex-start"
        rowSpacing={3}
        columnSpacing={{ xs: 2, md: 4 }}
      >
        {/* first row */}
        <>
          <Grid item xs={12} md={4}>
            <CustomTextField
              name="name"
              value={values.applicant.name}
              handleChange={handleChange}
              fullWidth
              label="Name"
              // helperText="As per aadhaar card"
              error={errors.name}
              required
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <CustomDatePicker
              value={values.applicant.birthDate}
              handleChange={(val: Date | null) =>
                handleDateChange("birthDate", val)
              }
              label="Date of Birth"
              error={errors.birthDate}
              maxDate={new Date(`12/31/${new Date().getFullYear() - 18}`)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <CustomSelect
              name="gender"
              label="Gender"
              value={values.applicant.gender}
              items={[
                { value: "M", label: "Male" },
                { value: "F", label: "Female" },
              ]}
              handleChange={handleChange}
              required
              error={errors.gender}
            />
            {/* <CustomRadioButtons
              name="gender"
              label="Gender"
              value={values.applicant.gender}
              options={[
                { value: "M", label: "Male" },
                { value: "F", label: "Female" },
              ]}
              handleChange={handleChange}
              required
              error={errors.gender}
            /> */}
          </Grid>
        </>

        {/* second row */}
        <>
          <Grid item xs={12} md={4}>
            <CustomSelect
              name="maritalStatus"
              label="Marital Status"
              value={values.applicant.maritalStatus}
              items={[
                { value: "M", label: "Married" },
                { value: "U", label: "Unmarried" },
              ]}
              handleChange={handleChange}
              required
              error={errors.maritalStatus}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <CustomTextField
              name="phone"
              value={values.applicant.phone}
              handleChange={handleChange}
              fullWidth
              label="Mobile / Telegram No."
              required
              error={errors.phone}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <CustomTextField
              name="email"
              value={values.applicant.email}
              handleChange={handleChange}
              fullWidth
              label="Email ID"
              // required
              error={errors.email}
            />
          </Grid>
        </>

        {/* 3rd row */}
        <>
          {/* <Grid item xs={12} md={4}>
            <CustomAutocomplete
              label="Designation"
              options={designations}
              value={
                values.applicant.designationId
                  ? values.applicant.designationId.name
                  : null
              }
              setValue={setDesignation}
              required
              error={errors.designationId}
            />
          </Grid> */}

          <Grid item xs={12} md={4}>
            <CustomAutocomplete
              label="Job Category"
              options={jobTypes}
              value={
                values.applicant.headline
                  ? values.applicant.headline.name
                  : null
              }
              setValue={setJobType}
              required
              error={errors.headline}
            />
            {/* <CustomRadioButtons
              name="headline"
              label="Resume Headline"
              value={values.applicant.headline}
              options={[
                { value: "Teaching", label: "Teaching" },
                { value: "Non-teaching", label: "Non-teaching" },
              ]}
              handleChange={handleChange}
              required
              error={errors.headline}
            /> */}
          </Grid>

          <Grid item xs={12} md={4}>
            <CustomAutocomplete
              label="Country"
              options={countries}
              value={
                values.applicant.country ? values.applicant.country.name : null
              }
              setValue={setCountry}
              required
              error={errors.country}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <CustomAutocomplete
              label="Province"
              options={states}
              value={
                values.applicant.state ? values.applicant.state.name : null
              }
              setValue={setState}
              required={states.length > 0}
              error={errors.state}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <CustomAutocomplete
              label="City"
              options={cities}
              value={values.applicant.city ? values.applicant.city.name : null}
              setValue={setCity}
              required={cities.length > 0}
              error={errors.city}
            />
          </Grid>
        </>

        {/* 4th row */}
        <>
          <Grid item xs={12} md={4}>
            <CustomTextField
              name="street"
              value={values.applicant.street}
              handleChange={handleChange}
              fullWidth
              label="Address Line"
              required
              error={errors.street}
            />
          </Grid>
          {/* <Grid item xs={12} md={4}>
            <CustomTextField
              name="locality"
              value={values.applicant.locality}
              handleChange={handleChange}
              fullWidth
              label="Locality"
              required
              error={errors.locality}
            />
          </Grid> */}
          {/* <Grid item xs={12} md={4}>
            <CustomTextField
              name="pinCode"
              value={values.applicant.pinCode}
              handleChange={handleChange}
              fullWidth
              label="Pincode"
              // required
              error={errors.pinCode}
            />
          </Grid> */}
        </>

        {/* 5th row */}
        <Grid item xs={12}>
          <CustomTextField
            multiline
            rows={3}
            name="skills"
            value={values.applicant.skills}
            handleChange={handleChange}
            fullWidth
            label="Areas of your interest"
            required
            error={errors.skills}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

export default ApplicantDetailsForm;
